import React from 'react';
import Wahanda from 'common/wahanda';
import { Spinner } from 'components/common/Spinner';
import { PaymentOptions, Payments } from './PaymentOptions';
import { BankPayment } from './BankPayment';
import { StripeCheckoutNewFlow } from './StripeCheckoutNewFlow';

enum PaymentViews {
  PaymentOptions = 'PaymentOptions',
  BankPayment = 'BankPayment',
  iDeal = 'iDeal',
}

const SHOW_NEW_IDEAL_FLOW = 'TEF-2121-enable-iDeal-new-flow';
// TEF-4356: when direct checkout is enabled, skip the "how would you like to pay?" fork and send
// the partner straight to the Stripe hosted checkout. Rolled out per country and per supplier.
const INVOICE_DIRECT_CHECKOUT_BY_SUPPLIER_FF =
  'TEF-4356-invoice-direct-checkout-with-bank-transfer-supplier';
const INVOICE_DIRECT_CHECKOUT_BY_COUNTRY_FF =
  'TEF-4356-invoice-direct-checkout-with-bank-transfer-country';

// Abort the checkout-session request if it hangs. In direct-checkout mode the spinner is the only
// view (no close button), so a stuck request would trap the partner; aborting lets the catch fall
// back to the fork instead.
const CHECKOUT_SESSION_TIMEOUT_MS = 20000;

const lang = Wahanda.lang.settings.supplier.payments.payNowDialog;

interface Props {
  idealSupported: boolean;
  hasBankDetails: boolean;
  doIdealPaymentRequest?: (props: { paymentAmount: number }) => void;
  idealPaymentInProgress: boolean;
  onClose: () => void;
  closingBalance: number;
  paymentReference: string;
  supplierId: number;
  paymentId: number;
}

interface State {
  activeView: PaymentViews;
  isRedirecting: boolean;
  directCheckoutFailed: boolean;
}

export class PaymentContainer extends React.PureComponent<Props, State> {
  private isUnmounted = false;

  private checkoutTimeoutId?: ReturnType<typeof setTimeout>;

  public state: State = {
    activeView: PaymentViews.PaymentOptions,
    isRedirecting: false,
    directCheckoutFailed: false,
  };

  public componentDidMount() {
    if (this.isDirectCheckoutEnabled()) {
      this.initiateStripeOCSCheckout();
    }
  }

  public componentWillUnmount() {
    this.isUnmounted = true;
    clearTimeout(this.checkoutTimeoutId);
  }

  private isDirectCheckoutEnabled(): boolean {
    return (
      Wahanda.Features.isEnabled(INVOICE_DIRECT_CHECKOUT_BY_SUPPLIER_FF) ||
      Wahanda.Features.isEnabled(INVOICE_DIRECT_CHECKOUT_BY_COUNTRY_FF)
    );
  }

  public paymentSelected = (paymentOption: Payments) => {
    if (paymentOption === Payments.stripe) {
      // "Pay online" always uses the Stripe hosted checkout (OCS).
      this.initiateStripeOCSCheckout();
      return;
    }

    const views: Record<Exclude<Payments, Payments.stripe>, PaymentViews> = {
      [Payments.bank]: PaymentViews.BankPayment,
      [Payments.ideal]: PaymentViews.iDeal,
    };
    this.setState({ activeView: views[paymentOption] });
  };

  private buildRedirectUrl(param: 'success' | 'canceled'): string {
    try {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set(param, 'true');
      return urlObj.toString();
    } catch {
      return window.location.href;
    }
  }

  private initiateStripeOCSCheckout = async () => {
    const { supplierId, paymentId } = this.props;

    this.setState({ isRedirecting: true });

    const abortController = new AbortController();
    this.checkoutTimeoutId = setTimeout(() => abortController.abort(), CHECKOUT_SESSION_TIMEOUT_MS);

    try {
      const response = await fetch(
        `/api/finance/supplier/${supplierId}/invoice/${paymentId}/checkout-url`,
        {
          method: 'POST',
          body: JSON.stringify({
            successUrl: this.buildRedirectUrl('success'),
            cancelUrl: this.buildRedirectUrl('canceled'),
          }),
          headers: { 'Content-Type': 'application/json' },
          signal: abortController.signal,
        },
      );

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const redirectUrl = await response.text();
      const target = new URL(redirectUrl);

      if (target.protocol !== 'https:') {
        throw new Error('Invalid redirect URL protocol');
      }

      window.location.assign(target.toString());
    } catch {
      if (!this.isUnmounted) {
        // On failure (including an aborted/timed-out request) fall back to the fork so the partner
        // still has a way to pay (even in direct-checkout mode, where the spinner was the only view).
        this.setState({ isRedirecting: false, directCheckoutFailed: true });
        Wahanda.ToastNotifications.sendMessage(lang.checkoutError, { variant: 'danger' });
      }
    } finally {
      clearTimeout(this.checkoutTimeoutId);
    }
  };

  public render() {
    const {
      onClose,
      closingBalance,
      paymentReference,
      idealPaymentInProgress,
      idealSupported,
      hasBankDetails,
    } = this.props;
    const { activeView, isRedirecting, directCheckoutFailed } = this.state;

    // Direct checkout: skip the fork. Show a spinner while the Stripe session is created and the
    // partner is redirected. If it fails, drop through to the fork below as a fallback.
    if (this.isDirectCheckoutEnabled() && !directCheckoutFailed) {
      return <Spinner size="medium" />;
    }

    switch (activeView) {
      case PaymentViews.PaymentOptions:
        return (
          <PaymentOptions
            idealSupported={idealSupported}
            hasBankDetails={hasBankDetails}
            paymentInProgress={idealPaymentInProgress || isRedirecting}
            onClose={onClose}
            onPaymentSelect={this.paymentSelected}
          />
        );
      case PaymentViews.BankPayment:
        return (
          <BankPayment
            paymentReference={paymentReference}
            closingBalance={closingBalance}
            onClose={onClose}
          />
        );
      case PaymentViews.iDeal: {
        if (Wahanda.Features.isEnabled(SHOW_NEW_IDEAL_FLOW)) {
          return <StripeCheckoutNewFlow closingBalance={closingBalance} />;
        }
        return null;
      }
    }
  }
}
